// Story 8.13 (ADO-2022), D-2 to D-5, D-7 — the Fulfilment card (Figma "Order Review · VC · Desktop
// 1440" 5006:3275 and its Collection, Cut-off, tablet and mobile variants).
//
//   <FulfilmentCard depotId={depotId} />
//
// Sits between the Submit card and the lines card, and only when the cart offers delivery and
// collection options (`useFulfilmentOptions(...).offered`: a Value Centre cart, not LSN). It reads
// nothing and shows nothing otherwise (NDC, direct delivery, LSN: AC4).
//
// What it offers (each control offers only valid values; the server is the only rule, D-6):
//   - Delivery (the default) with "Delivery date", pre-filled with the next delivery, the calendar
//     enabling only the dates on offer (D-2). No date field when the store has no schedule at the
//     depot (D-3): the order then takes the depot's next run.
//   - Collection: "Collection date" (from today; today only while it has a slot left), "Collection
//     time" (the fixed slots; today's start after now) and "Contact name" (D-4). Default: tomorrow
//     at 10:00, at any time of day.
//   - "Special instructions": a toggle, off by default; on, a text area (D-5).
//
// The date fields behave as the Previous Orders filter date fields: wider than a phone the
// calendar opens under the field; on a phone it opens as a bottom sheet titled with the field's
// label, and picking a day closes it and returns focus to the field.
//
// Held means touched (D-7), per cart: every read and write is keyed by the cart (`cartKey`), never
// the depot. What the Shopper changes is written to the submission store (`setFulfilmentField`);
// the defaults are shown, never held, with one exception: choosing Collection holds the date and
// time the card shows at that moment, so a send with the card off screen (the /choose "Submit
// now") carries them. What the card SHOWS, defaults included, is
// registered (`setFulfilmentShown`) while it is on screen, and every Submit button on the page
// sends exactly that. An untouched date follows the options, so a passed cut-off rolls it
// to the next delivery; a touched one stays and is re-checked at send.
//
// The options follow the clock: they are read again at each half hour (`useFulfilmentOptions`),
// so today's slots and the collection calendar's first day move as time passes, and an untouched
// default follows them (tomorrow at 10:00 is tomorrow again after midnight). A held collection
// date or time the fresh options no longer offer (a date now in the past, today's slot once it
// passed) is kept as the Shopper chose it and flagged on its field with the server's wording,
// never changed without telling them; the send still carries it, and the server refuses it (D-6).
//
// A refused choice (`error.fields`) is shown on each field. A refusal is retired once the card no
// longer shows the field, or shows another value than the refused send carried (an untouched
// default the re-read options moved, D-2). A new refusal moves focus to the first invalid field on
// the card; one already present when the card mounts is shown without moving
// focus (the 8.14 reference-field precedent). Every control is read-only while the order sends.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { FulfilmentOptions } from '../../api/basket.client';
import { cartTargetKey } from '../../api/cart-target';
import type {
  FulfilmentField,
  FulfilmentRequestFields,
  InvalidFulfilmentField,
} from '../../api/orders.client';
import { useSessionStore } from '../../auth/useSessionStore';
import { useCartTargetAt } from '../../basket/sessionCart';
import { useDepotCutOff } from '../../basket/useDepotCutOff';
import { cutOffMark, useFulfilmentOptions } from '../../basket/useFulfilmentOptions';
import { useIsNarrowViewport } from '../../basket/useIsNarrowViewport';
import { useOrderSubmissionStore } from '../../basket/useOrderSubmissionStore';
import {
  DateField,
  DatePicker,
  FormField,
  FormTextArea,
  Listbox,
  Modal,
  RadioGroup,
  Toggle,
} from '../../design-system';
import { addDays } from '../../design-system/DatePicker/calendar';
import styles from './FulfilmentCard.module.css';

const HEADING_ID = 'fulfilment-heading';

/** The ids the card's controls carry, in the order they appear: a new refusal focuses the first
 *  of the refused ones. */
const FIELD_IDS: Readonly<Record<Exclude<FulfilmentField, 'fulfilmentMethod'>, string>> = {
  deliveryDate: 'fulfilment-delivery-date',
  collectionDate: 'fulfilment-collection-date',
  collectionTime: 'fulfilment-collection-time',
  contactName: 'fulfilment-contact-name',
  specialInstructions: 'fulfilment-special-instructions',
};
const METHOD_NAME = 'fulfilment-method';
const FIELD_ORDER: readonly FulfilmentField[] = [
  'fulfilmentMethod',
  'deliveryDate',
  'collectionDate',
  'collectionTime',
  'contactName',
  'specialInstructions',
];

/** The control a refusal of `field` moves focus to: the checked radio for the method. */
function controlFor(field: FulfilmentField): HTMLElement | null {
  if (field === 'fulfilmentMethod') {
    return document.querySelector<HTMLInputElement>(`input[name="${METHOD_NAME}"]:checked`);
  }
  return document.getElementById(FIELD_IDS[field]);
}

/** The date field whose calendar the phone sheet shows. */
type CalendarField = 'deliveryDate' | 'collectionDate';
const CALENDAR_LABELS: Readonly<Record<CalendarField, string>> = {
  deliveryDate: 'Delivery date',
  collectionDate: 'Collection date',
};

const METHOD_OPTIONS = [
  { value: 'DELIVERY', label: 'Delivery' },
  { value: 'COLLECTION', label: 'Collection' },
];

/** The frames' line under the heading: the Value Centre frames name the cart, the Foodservice
 *  frame does not. */
function introFor(foodservice: boolean): string {
  return `Delivery and collection options for this ${foodservice ? '' : 'Value Centre '}cart. Dates beyond the next available window are allowed.`;
}

/** The collection time the card shows before the Shopper changes it (D-4). */
const DEFAULT_COLLECTION_TIME = '10:00';

/** The collection the card shows before the Shopper changes it (D-4): tomorrow at 10:00, at any
 *  time of day. */
function defaultCollection(options: FulfilmentOptions): { date: string; time: string } {
  return { date: addDays(options.today, 1), time: DEFAULT_COLLECTION_TIME };
}

/** What the server says of a collection date in the past, or of today's slot once it passed
 *  (order-capture's `FULFILMENT_MESSAGES`): shown on a held value the options no longer offer. */
const COLLECTION_DATE_PAST = 'The collection date cannot be in the past.';
const COLLECTION_TIME_PASSED = 'This collection time has passed. Choose a later time.';

/** The slots a day offers: today's from the first still open, any other day's all of them. */
function slotsFor(options: FulfilmentOptions, date: string): readonly string[] {
  if (date !== options.today) return options.collectionTimeSlots;
  const first = options.firstCollectionSlotToday;
  return first === null ? [] : options.collectionTimeSlots.filter((slot) => slot >= first);
}

export interface FulfilmentCardProps {
  readonly depotId: string;
}

export function FulfilmentCard({ depotId }: FulfilmentCardProps) {
  const cart = useCartTargetAt(depotId);
  const cutOff = useDepotCutOff(cart);
  const options = useFulfilmentOptions(cart, cutOffMark(cutOff.status));
  if (options === null || !options.offered) return null;
  return <OfferedFulfilmentCard depotId={depotId} options={options} />;
}

function OfferedFulfilmentCard({
  depotId,
  options,
}: {
  readonly depotId: string;
  readonly options: FulfilmentOptions;
}) {
  const cart = useCartTargetAt(depotId);
  const cartKey = cart === null ? depotId : cartTargetKey(cart);
  const touched: FulfilmentRequestFields = useOrderSubmissionStore(
    (s) => s.fulfilmentByCart[cartKey] ?? EMPTY,
  );
  const setField = useOrderSubmissionStore((s) => s.setFulfilmentField);
  const setShown = useOrderSubmissionStore((s) => s.setFulfilmentShown);
  const retireRefusals = useOrderSubmissionStore((s) => s.retireFulfilmentRefusals);
  const sending = useOrderSubmissionStore((s) => s.cartKey === cartKey && s.phase === 'sending');
  const refusals = useOrderSubmissionStore((s) =>
    s.phase === 'failed' && s.cartKey === cartKey ? (s.error?.fields ?? null) : null,
  );
  const refusedSend = useOrderSubmissionStore((s) =>
    s.phase === 'failed' && s.cartKey === cartKey ? (s.error?.fulfilmentSent ?? null) : null,
  );
  const foodservice = useSessionStore(
    (s) =>
      s.activeDepot === depotId && s.activeTradingAccount?.tradingAccountType === 'FOODSERVICE',
  );

  const method = touched.fulfilmentMethod ?? 'DELIVERY';
  const collectionDefault = defaultCollection(options);
  const deliveryDate =
    'deliveryDate' in touched ? (touched.deliveryDate ?? '') : (options.deliveryDates[0] ?? '');
  const collectionDate = touched.collectionDate ?? collectionDefault.date;
  // An untouched time keeps the default slot while the chosen day still offers it, else takes
  // that day's first slot.
  const daySlots = slotsFor(options, collectionDate);
  const collectionTime =
    touched.collectionTime ??
    (daySlots.includes(collectionDefault.time) ? collectionDefault.time : (daySlots[0] ?? ''));
  const contactName = touched.contactName ?? '';
  const instructionsOn = touched.specialInstructions !== undefined;
  const instructions = touched.specialInstructions ?? '';

  // What the card shows, as the send carries it (D-7).
  const shownBody = useMemo((): FulfilmentRequestFields => {
    const notes = instructionsOn ? { specialInstructions: instructions } : {};
    if (method === 'COLLECTION') {
      return {
        fulfilmentMethod: 'COLLECTION',
        collectionDate,
        collectionTime,
        contactName,
        ...notes,
      };
    }
    return {
      fulfilmentMethod: 'DELIVERY',
      ...(options.deliveryDates.length > 0 && deliveryDate !== '' ? { deliveryDate } : {}),
      ...notes,
    };
  }, [
    method,
    collectionDate,
    collectionTime,
    contactName,
    deliveryDate,
    instructionsOn,
    instructions,
    options.deliveryDates.length,
  ]);

  // Keyed on the cart, not the depot: a change of Trading Account at the same depot (8.4) is a
  // change of cart, and the cleanup must clear the cart the card was showing.
  useEffect(() => {
    setShown(cartKey, shownBody);
  }, [cartKey, shownBody, setShown]);
  useEffect(() => () => setShown(cartKey, null), [cartKey, setShown]);

  // The fields on screen. A refusal of any other field (a delivery date once Collection is
  // chosen, say) is retired: the send will not carry that field, and nothing would show it. So is
  // a refusal of a value the card no longer shows (an empty field and an absent one are the same).
  const shownFields = useMemo((): readonly FulfilmentField[] => {
    const methodFields: readonly FulfilmentField[] =
      method === 'COLLECTION'
        ? ['collectionDate', 'collectionTime', 'contactName']
        : options.deliveryDates.length > 0
          ? ['deliveryDate']
          : [];
    return [
      'fulfilmentMethod',
      ...methodFields,
      ...(instructionsOn ? (['specialInstructions'] as const) : []),
    ];
  }, [method, options.deliveryDates.length, instructionsOn]);
  useEffect(() => {
    if (refusals === null) return;
    const moot = refusals
      .map((refusal) => refusal.field)
      .filter(
        (field) =>
          !shownFields.includes(field) ||
          (refusedSend !== null &&
            field !== 'fulfilmentMethod' &&
            (shownBody[field] ?? '') !== (refusedSend[field] ?? '')),
      );
    if (moot.length > 0) retireRefusals(cartKey, moot);
  }, [cartKey, refusals, refusedSend, shownFields, shownBody, retireRefusals]);

  // A held collection value the options no longer offer (the clock moved past it): flagged, not
  // changed. A refusal of the field, when there is one, is shown instead.
  const noLongerOffered: Partial<Record<FulfilmentField, string>> =
    method !== 'COLLECTION'
      ? {}
      : {
          ...(touched.collectionDate !== undefined &&
          touched.collectionDate !== '' &&
          touched.collectionDate < options.today
            ? { collectionDate: COLLECTION_DATE_PAST }
            : {}),
          ...(touched.collectionTime !== undefined &&
          collectionDate === options.today &&
          !daySlots.includes(touched.collectionTime)
            ? { collectionTime: COLLECTION_TIME_PASSED }
            : {}),
        };
  const messageFor = (field: FulfilmentField): string | undefined =>
    refusals?.find((refusal: InvalidFulfilmentField) => refusal.field === field)?.message ??
    noLongerOffered[field];

  // A new refusal takes the Shopper to its first field; one present at mount moves nothing.
  const focusedRefusals = useRef(refusals);
  useEffect(() => {
    if (refusals === null || refusals === focusedRefusals.current) return;
    focusedRefusals.current = refusals;
    const first = FIELD_ORDER.find((field) => refusals.some((refusal) => refusal.field === field));
    if (first !== undefined) controlFor(first)?.focus();
  }, [refusals]);

  const set = (field: FulfilmentField, value: string | undefined) =>
    setField(cartKey, field, value);
  // Today takes a collection only while it has a slot left (D-4).
  const isCollectionDayEnabled = (day: string) =>
    day !== options.today || options.firstCollectionSlotToday !== null;
  // A new collection day that does not offer the held time (today, after it) moves the time to
  // the day's first open slot, as an untouched time would.
  const setCollectionDate = (day: string) => {
    set('collectionDate', day);
    const slots = slotsFor(options, day);
    if (touched.collectionTime !== undefined && !slots.includes(touched.collectionTime)) {
      set('collectionTime', slots[0]);
    }
  };

  // On a phone each date field hands its calendar to the sheet. Focus goes into the calendar's
  // roving day on open and back to the field on close (this effect runs after the sheet's own
  // focus restore, so it wins when the field was not the focused element).
  const phone = useIsNarrowViewport();
  const [calendar, setCalendar] = useState<CalendarField | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<CalendarField | null>(null);
  // The field the sheet last showed, so it keeps its title and calendar while it fades out.
  const lastCalendar = useRef<CalendarField>('deliveryDate');
  if (calendar !== null) lastCalendar.current = calendar;
  const sheetField = calendar ?? lastCalendar.current;
  useEffect(() => {
    if (calendar !== null) {
      calendarRef.current?.querySelector<HTMLElement>('[data-focused="true"]')?.focus();
      return;
    }
    if (returnFocusTo.current === null) return;
    document.getElementById(FIELD_IDS[returnFocusTo.current])?.focus();
    returnFocusTo.current = null;
  }, [calendar]);
  const sheetValue = sheetField === 'deliveryDate' ? deliveryDate : collectionDate;
  const openCalendar = (field: CalendarField) =>
    phone ? { onOpenCalendar: () => setCalendar(field) } : {};
  const closeCalendar = () => {
    returnFocusTo.current = calendar;
    setCalendar(null);
  };
  const timeOptions = daySlots.map((slot) => ({
    value: slot,
    label: slot,
  }));

  return (
    <section className={styles.card} aria-labelledby={HEADING_ID}>
      <h2 id={HEADING_ID} className={styles.heading}>
        Fulfilment
      </h2>
      <p className={styles.intro}>{introFor(foodservice)}</p>
      {/* One fieldset, so every control is read-only while the order sends. It carries no name of
          its own: the heading names the region (as SubmitOrderCard's does) and the method group. */}
      <fieldset className={styles.fieldset} disabled={sending}>
        <RadioGroup
          labelledBy={HEADING_ID}
          orientation="horizontal"
          name={METHOD_NAME}
          value={method}
          onChange={(value) => {
            set('fulfilmentMethod', value);
            // Collection holds what the card shows (D-7); Delivery holds nothing more.
            if (value === 'COLLECTION') {
              set('collectionDate', collectionDate);
              set('collectionTime', collectionTime);
            }
          }}
          options={METHOD_OPTIONS}
          {...errorProp(messageFor('fulfilmentMethod'))}
        />
        {method === 'DELIVERY' ? (
          options.deliveryDates.length > 0 ? (
            <div className={styles.dateRow}>
              <DateField
                id={FIELD_IDS.deliveryDate}
                label="Delivery date"
                clearable={false}
                value={deliveryDate === '' ? null : deliveryDate}
                today={options.today}
                isDayEnabled={(day) => options.deliveryDates.includes(day)}
                onChange={(day) => set('deliveryDate', day ?? '')}
                {...openCalendar('deliveryDate')}
                {...errorProp(messageFor('deliveryDate'))}
              />
            </div>
          ) : null
        ) : (
          <>
            <div className={styles.collectionRow}>
              <DateField
                id={FIELD_IDS.collectionDate}
                label="Collection date"
                clearable={false}
                value={collectionDate === '' ? null : collectionDate}
                today={options.today}
                min={options.today}
                isDayEnabled={isCollectionDayEnabled}
                onChange={(day) => setCollectionDate(day ?? '')}
                {...openCalendar('collectionDate')}
                {...errorProp(messageFor('collectionDate'))}
              />
              {/* The DS Listbox, whose popup matches the frames' open time list (scrolled to the
                  chosen slot, a check on it), where a native select's could not. */}
              <Listbox
                id={FIELD_IDS.collectionTime}
                label="Collection time"
                options={timeOptions}
                value={collectionTime}
                onChange={(value) => set('collectionTime', value)}
                // The trigger is a div, which the fieldset's `disabled` does not reach.
                disabled={sending}
                required
                {...errorProp(messageFor('collectionTime'))}
              />
            </div>
            <div className={styles.contactRow}>
              <FormField
                id={FIELD_IDS.contactName}
                label="Contact name"
                placeholder="Who should we ask for on collection?"
                autoComplete="name"
                value={contactName}
                maxLength={options.contactNameMaxLength}
                onChange={(event) => set('contactName', event.target.value)}
                required
                aria-required
                {...errorProp(messageFor('contactName'))}
              />
            </div>
          </>
        )}
        <hr className={styles.divider} />
        <Toggle
          label="Special instructions"
          checked={instructionsOn}
          onChange={(on) => set('specialInstructions', on ? instructions : undefined)}
          spreadBelowDesktop
        />
        {instructionsOn ? (
          <FormTextArea
            id={FIELD_IDS.specialInstructions}
            aria-label="Special instructions"
            placeholder="Optional notes for the depot (gate codes, preferred bay, etc.)"
            value={instructions}
            maxLength={options.specialInstructionsMaxLength}
            onChange={(event) => set('specialInstructions', event.target.value)}
            aria-required
            {...errorProp(messageFor('specialInstructions'))}
          />
        ) : null}
      </fieldset>
      <Modal
        open={calendar !== null}
        onClose={closeCalendar}
        title={CALENDAR_LABELS[sheetField]}
        placement="bottom"
        sheetGutter="compact"
      >
        <div ref={calendarRef}>
          <DatePicker
            variant="inline"
            aria-label={CALENDAR_LABELS[sheetField]}
            value={sheetValue === '' ? null : sheetValue}
            today={options.today}
            {...(sheetField === 'deliveryDate'
              ? { isDayEnabled: (day: string) => options.deliveryDates.includes(day) }
              : { min: options.today, isDayEnabled: isCollectionDayEnabled })}
            onSelect={(day) => {
              if (sheetField === 'collectionDate') setCollectionDate(day);
              else set(sheetField, day);
              closeCalendar();
            }}
          />
        </div>
      </Modal>
    </section>
  );
}

const EMPTY: FulfilmentRequestFields = {};

/** `{ error }` only when there is one (`exactOptionalPropertyTypes`). */
function errorProp(message: string | undefined): { error?: string } {
  return message === undefined ? {} : { error: message };
}
